import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatActionList, MatListItem } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';

import { isSameSection, MeetingSection } from '@ninety/platform/models/meetings/meeting-section';
import { UserAvatarInfo } from '@ninety/platform/models/user/user-model-state';
import { TerraAvatarModule, TerraBadgeModule } from '@ninety/terra';
import { ClockTimerComponent } from '@ninety/ui/core';

/**
 * Presentational section list for {@link MeetingSidenavV2Component}. Pure inputs → outputs: no
 * store, no services, no side effects — the container owns navigation and broadcasts.
 */
@Component({
  standalone: true,
  selector: 'ninety-meeting-sidenav-section-list',
  templateUrl: './meeting-sidenav-section-list.component.html',
  styleUrls: ['./meeting-sidenav-section-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatActionList, MatListItem, MatTooltip, ClockTimerComponent, TerraAvatarModule, TerraBadgeModule],
})
export class MeetingSidenavSectionListComponent {
  readonly sections = input<MeetingSection[]>([]);
  /** Section the meeting is on — drives the active row. */
  readonly currentSection = input<MeetingSection | null>(null);

  /**
   * Section the *facilitator* is on. Null when autofollow is off or nobody is presenting, which
   * collapses the avatar gutter and leaves the flag-off row layout unchanged.
   */
  readonly presenterSection = input<MeetingSection | null>(null);
  readonly presenterAvatar = input<UserAvatarInfo | null>(null);
  /** This user has drifted off the facilitator's section, so their row offers a way back. */
  readonly outOfSyncWithPresenter = input(false);

  readonly activeSectionElapsed = input<number | null>(null);

  /** Fired with the ordered-list index (same as v1 `$index`) when a row is clicked. */
  readonly sectionSelected = output<{ section: MeetingSection; index: number }>();
  /** Fired instead of `sectionSelected` when the clicked row is the facilitator's re-sync prompt. */
  readonly followPresenterSelected = output<void>();

  /** Sorted by ordinal so a unsorted store/service array still paints top-to-bottom correctly. */
  protected readonly orderedSections = computed(() =>
    [...this.sections()].sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
  );

  protected isActive(section: MeetingSection): boolean {
    return isSameSection(section, this.currentSection());
  }

  protected elapsedFor(section: MeetingSection): number | null {
    const stored = section.elapsedTime ?? 0;
    if (this.isActive(section)) return this.activeSectionElapsed() ?? stored;

    return stored || null;
  }

  protected isPresenterSection(section: MeetingSection): boolean {
    return isSameSection(section, this.presenterSection());
  }

  /** The re-sync affordance only exists on the facilitator's row, and only while out of sync. */
  protected offersResync(section: MeetingSection): boolean {
    return this.outOfSyncWithPresenter() && this.isPresenterSection(section);
  }

  protected onRowClicked(section: MeetingSection, index: number): void {
    if (this.offersResync(section)) this.followPresenterSelected.emit();
    else this.sectionSelected.emit({ section, index });
  }
}
